import { type Bounds, bounds as boundsOps } from '@/domain/geometry';
import { clamp, type Mat3, mat, type Vec2, vec } from '@/domain/math';

/**
 * The viewport camera.
 *
 * `x, y` are the **world** coordinates displayed at the centre of the viewport, and
 * `zoom` is screen pixels per world unit. Anchoring the camera to the viewport centre
 * rather than its top-left corner is what makes zoom-to-cursor, fit-to-content and
 * window resizing behave correctly without special cases — resizing the window then
 * keeps the same content centred instead of sliding it.
 */
export interface Camera {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}

/** Viewport size in CSS pixels. The device pixel ratio is handled by `LayerStack`. */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 200;

export const DEFAULT_CAMERA: Camera = Object.freeze({ x: 0, y: 0, zoom: 1 });

export function camera(x: number, y: number, zoom: number): Camera {
  return { x, y, zoom: clamp(zoom, MIN_ZOOM, MAX_ZOOM) };
}

/**
 * World → screen transform.
 *
 * The `-zoom` in the `d` slot is the y-axis flip. World space is y-up because that is
 * what the mathematics requires; screen space is y-down because that is what the canvas
 * requires. Confining the flip to this one matrix is what lets every geometry routine
 * stay in ordinary mathematical orientation.
 *
 * The result is in `ctx.setTransform` order, so it can be handed to the canvas with no
 * conversion in the frame loop.
 */
export function worldToScreen(c: Camera, viewport: Viewport): Mat3 {
  return mat.mat3(
    c.zoom,
    0,
    0,
    -c.zoom,
    viewport.width / 2 - c.x * c.zoom,
    viewport.height / 2 + c.y * c.zoom
  );
}

export function screenToWorld(c: Camera, viewport: Viewport): Mat3 {
  const inverse = mat.invert(worldToScreen(c, viewport));
  // `zoom` is clamped away from zero, so the transform is never singular.
  return inverse ?? mat.IDENTITY;
}

export function toScreen(c: Camera, viewport: Viewport, world: Vec2): Vec2 {
  return mat.transformPoint(worldToScreen(c, viewport), world);
}

export function toWorld(c: Camera, viewport: Viewport, screen: Vec2): Vec2 {
  return mat.transformPoint(screenToWorld(c, viewport), screen);
}

/**
 * Converts a screen-pixel tolerance into world units.
 *
 * Every hit test uses this. Without it, picking would get harder as the user zooms out
 * and trivially easy zoomed in; with it, a click feels identical at every zoom level.
 */
export function screenToWorldDistance(c: Camera, pixels: number): number {
  return pixels / c.zoom;
}

/** Moves the camera by a screen-space delta — the direct translation of a pan gesture. */
export function panByScreen(c: Camera, dxScreen: number, dyScreen: number): Camera {
  return {
    x: c.x - dxScreen / c.zoom,
    // Negated a second time by the y-flip, so a downward drag moves content down.
    y: c.y + dyScreen / c.zoom,
    zoom: c.zoom,
  };
}

/**
 * Zooms while keeping the world point under `anchorScreen` pinned to that pixel.
 *
 * This is the behaviour of every professional editor and the reason zooming feels
 * controlled rather than disorienting: the thing under the cursor stays under the
 * cursor. Zooming about the viewport centre instead is the classic amateur version.
 */
export function zoomAt(c: Camera, viewport: Viewport, anchorScreen: Vec2, factor: number): Camera {
  const nextZoom = clamp(c.zoom * factor, MIN_ZOOM, MAX_ZOOM);
  if (nextZoom === c.zoom) return c;

  const anchorWorld = toWorld(c, viewport, anchorScreen);
  const zoomed: Camera = { x: c.x, y: c.y, zoom: nextZoom };
  const anchorAfter = toWorld(zoomed, viewport, anchorScreen);

  // Shift the camera by however far the anchor drifted.
  return {
    x: c.x + (anchorWorld.x - anchorAfter.x),
    y: c.y + (anchorWorld.y - anchorAfter.y),
    zoom: nextZoom,
  };
}

/** The world-space rectangle currently visible. Used to cull off-screen entities. */
export function visibleBounds(c: Camera, viewport: Viewport): Bounds {
  const halfWidth = viewport.width / 2 / c.zoom;
  const halfHeight = viewport.height / 2 / c.zoom;
  return {
    minX: c.x - halfWidth,
    minY: c.y - halfHeight,
    maxX: c.x + halfWidth,
    maxY: c.y + halfHeight,
  };
}

/**
 * Frames `target` with `padding` screen pixels of margin on every side.
 *
 * An empty or zero-extent target keeps the current zoom and merely centres, rather than
 * dividing by zero and flinging the camera to infinity — fitting a single point is a
 * perfectly reasonable thing for a user to ask for.
 */
export function fitBounds(c: Camera, viewport: Viewport, target: Bounds, padding = 40): Camera {
  if (boundsOps.isEmpty(target)) return c;

  const centre = boundsOps.center(target);
  const width = boundsOps.width(target);
  const height = boundsOps.height(target);

  const availableWidth = Math.max(1, viewport.width - padding * 2);
  const availableHeight = Math.max(1, viewport.height - padding * 2);

  if (width <= 0 && height <= 0) {
    return { x: centre.x, y: centre.y, zoom: c.zoom };
  }

  const zoom = clamp(
    Math.min(
      width > 0 ? availableWidth / width : Infinity,
      height > 0 ? availableHeight / height : Infinity
    ),
    MIN_ZOOM,
    MAX_ZOOM
  );

  return { x: centre.x, y: centre.y, zoom };
}

export function equals(a: Camera, b: Camera): boolean {
  return a.x === b.x && a.y === b.y && a.zoom === b.zoom;
}

/** Screen-space distance between two world points, without allocating a transform. */
export function screenDistance(c: Camera, a: Vec2, b: Vec2): number {
  return vec.distance(a, b) * c.zoom;
}
